import { api, guard, unwrap } from './client'

/**
 * Which repositories the page asks for.
 *
 * 'all' is everything the connected account can reach — its own repositories, its
 * organisations', and ones it collaborates on — which is what a person means when they open
 * a page called Repositories. Sent explicitly rather than relying on the server's default,
 * so the intent is visible at the call site.
 */
type Affiliation = 'owner' | 'collaborator' | 'organization_member' | 'all'

export const repositoriesApi = {
  list: (
    options: { includeArchived?: boolean; limit?: number; affiliation?: Affiliation } = {},
  ) =>
    guard(async () =>
      unwrap(
        await api.GET('/api/repositories', {
          params: {
            query: {
              include_archived: options.includeArchived ?? false,
              affiliation: options.affiliation ?? 'all',
              limit: options.limit ?? 50,
            },
          },
        }),
      ),
    ),

  detail: (owner: string, repo: string) =>
    guard(async () =>
      unwrap(await api.GET('/api/repositories/{owner}/{repo}', { params: { path: { owner, repo } } })),
    ),

  issues: (
    owner: string,
    repo: string,
    options: { state?: 'open' | 'closed' | 'all'; labels?: string[]; limit?: number } = {},
  ) =>
    guard(async () =>
      unwrap(
        await api.GET('/api/repositories/{owner}/{repo}/issues', {
          params: {
            path: { owner, repo },
            query: {
              state: options.state ?? 'open',
              labels: options.labels,
              limit: options.limit ?? 50,
            },
          },
        }),
      ),
    ),

  labels: (owner: string, repo: string) =>
    guard(async () =>
      unwrap(
        await api.GET('/api/repositories/{owner}/{repo}/labels', {
          params: { path: { owner, repo } },
        }),
      ),
    ),
}
